/**
 * Aircraft airframe → solid silhouette resolver (Q.1 Stage 4; re-drawn to the
 * FR24 quality bar in #201 — see docs/design-refs/fr24-aircraft-icons-acceptance.md).
 *
 * The live aircraft layer (R.1b) carries a free-text `aircraft_type` that is
 * either a human label seeded from the watchlist ('CL-415', 'CH-149 Cormorant',
 * 'PC-12 NG', 'King Air 350') or an ICAO type designator captured live from
 * ADS-B ('CL2T', 'C130', 'PC12', 'B350', 'AS50'). `resolveSilhouette` maps both
 * forms to one of 18 in-house top-down silhouettes covering the full
 * response_assets fleet plus civilian fallbacks, degrading to a generic
 * fixed-wing / jet / rotor / unknown marker rather than ever rendering blank.
 *
 * Silhouettes are nose-up (north) so a Cesium billboard `rotation` of the
 * negated compass heading points the airframe along its live track.
 *
 * COLOUR CONTRACT (#201 — FOUNDER RULING 2026-06-11, option B "pure
 * one-colour"): every silhouette is a constant gold body (#fbbf24) + dark
 * union outline (#0f172a) + soft drop shadow baked into the SVG, with NO
 * variable colour token at all — the marker carries no per-role accent.
 * Founder reasoning (recorded on tracker row #201): operators won't memorize
 * a 10-colour code; clicking the aircraft answers what/where-from/where-to
 * better than colour-matching against the legend. Role identity lives in the
 * panel badge + legend role swatches (locked styles/aircraftRoles palette,
 * unchanged). Assets are generated, not hand-edited: see
 * scripts/gen-aircraft-icons.mjs (clean-room provenance record — no GPL
 * tar1090 / dump1090-fa path data, no FR24 artwork).
 */
import { kindFromType, type AircraftKind } from './aircraftStyles';

import cc130 from './assets/aircraft/cc130.svg?raw';
import cc138 from './assets/aircraft/cc138.svg?raw';
import cc115 from './assets/aircraft/cc115.svg?raw';
import cc177 from './assets/aircraft/cc177.svg?raw';
import cc330 from './assets/aircraft/cc330.svg?raw';
import waterbomber from './assets/aircraft/waterbomber.svg?raw';
import at802 from './assets/aircraft/at802.svg?raw';
import pc12 from './assets/aircraft/pc12.svg?raw';
import kingair350 from './assets/aircraft/kingair350.svg?raw';
import fixedwingGeneric from './assets/aircraft/fixedwing-generic.svg?raw';
import jetGeneric from './assets/aircraft/jet-generic.svg?raw';
import turbopropGeneric from './assets/aircraft/turboprop-generic.svg?raw';
import ch149 from './assets/aircraft/ch149.svg?raw';
import ch146 from './assets/aircraft/ch146.svg?raw';
import ch147 from './assets/aircraft/ch147.svg?raw';
import lightheli from './assets/aircraft/lightheli.svg?raw';
import rotorGeneric from './assets/aircraft/rotor-generic.svg?raw';
import unknown from './assets/aircraft/unknown.svg?raw';

export type SilhouetteKey =
  | 'cc130'
  | 'cc138'
  | 'cc115'
  | 'cc177'
  | 'cc330'
  | 'waterbomber'
  | 'at802'
  | 'pc12'
  | 'kingair350'
  | 'fixedwing-generic'
  | 'jet-generic'
  | 'turboprop-generic'
  | 'ch149'
  | 'ch146'
  | 'ch147'
  | 'lightheli'
  | 'rotor-generic'
  | 'unknown';

/** Raw SVG markup per silhouette key (one-colour gold body + outline baked
 *  in). Exported for tests + the future R.1.5 hero strip. */
export const AIRCRAFT_SILHOUETTE: Record<SilhouetteKey, string> = {
  cc130,
  cc138,
  cc115,
  cc177,
  cc330,
  waterbomber,
  at802,
  pc12,
  kingair350,
  'fixedwing-generic': fixedwingGeneric,
  'jet-generic': jetGeneric,
  'turboprop-generic': turbopropGeneric,
  ch149,
  ch146,
  ch147,
  lightheli,
  'rotor-generic': rotorGeneric,
  unknown,
};

/** Human label per silhouette, for the side panel / a future legend. */
export const SILHOUETTE_LABEL: Record<SilhouetteKey, string> = {
  cc130: 'CC-130 Hercules',
  cc138: 'CC-138 Twin Otter',
  cc115: 'CC-115 Buffalo',
  cc177: 'CC-177 Globemaster III',
  cc330: 'CC-330 Husky',
  waterbomber: 'CL-415 / CL-215T water bomber',
  at802: 'AT-802F Fire Boss',
  pc12: 'PC-12 Pilatus',
  kingair350: 'King Air 350',
  'fixedwing-generic': 'Fixed-wing',
  'jet-generic': 'Jet',
  'turboprop-generic': 'Twin turboprop',
  ch149: 'CH-149 Cormorant',
  ch146: 'CH-146 Griffon',
  ch147: 'CH-147F Chinook',
  lightheli: 'Light helicopter',
  'rotor-generic': 'Helicopter',
  unknown: 'Unknown aircraft',
};

/**
 * Ordered match rules against the normalised (upper-case, space/dash-stripped)
 * aircraft_type. First hit wins, so specific airframes precede the broad
 * water-bomber / type-family buckets. Patterns cover both the human label and
 * the ICAO designator for each airframe.
 */
const RULES: Array<[RegExp, SilhouetteKey]> = [
  // Tandem-rotor — check before generic Chinook 'CH' patterns.
  [/CH147|CHINOOK|^H47|^CH47/, 'ch147'],
  [/CH149|CORMORANT|EH101|AW101|^EH10/, 'ch149'],
  [/CH146|GRIFFON|BELL412|^B412|^BH12/, 'ch146'],
  // CCG light twin (R.1e) — 'Bell 429' / ICAO 'B429'. Precedes the generic
  // 'B'-prefix fixed-wing fallback, which would otherwise mis-class it.
  [/BELL429|^B429/, 'lightheli'],
  // Light EMS / utility rotorcraft (Airbus H-series, AStar, EC135/145).
  [/AS350|AS355|ASTAR|ECUREUIL|^AS50|^AS55|EC130|^EC30|H125|^H12[05]/, 'lightheli'],
  [/EC135|^EC35|EC145|^EC45|H135|H145|^H13|^H14|BK117|^BK11/, 'lightheli'],
  // Fixed-wing — specific types.
  [/CC130|HERCULES|^C130|^C30J|LOCKHEEDC/, 'cc130'],
  [/CC177|GLOBEMASTER|^C17$|^C17[^0-9]/, 'cc177'],
  [/CC330|HUSKY|A330|^A33/, 'cc330'],
  [/CC138|TWINOTTER|DHC6|^DH6|^DHC6/, 'cc138'],
  [/CC115|BUFFALO|DHC5|^DH5/, 'cc115'],
  [/AT802|AT8|FIREBOSS|AIRTRACTOR/, 'at802'],
  // Bizjets — must precede the water-bomber bucket: ICAO CL60 (Challenger
  // 600-series) shares the 'CL' prefix with the Canadair amphibians and used
  // to mis-resolve to 'waterbomber' via a ^CL6 pattern there (#201 fix).
  [/CHALLENGER|^CL60|^CL30|^CL35|^GLF|^GL[0-9]|LEARJET|^LJ[0-9]|GULFSTREAM|FALCON7X|^FA7X|^F2TH/, 'jet-generic'],
  [/CL415|CL215|^CL2|^CL4|^CL1T|CANADAIR/, 'waterbomber'],
  // RJ85/RJ100 airtanker (Avro regional jet conversion) — jet planform.
  [/AVRORJ|RJ85|RJ100|^RJ85|^RJ1H|BAE146|^B461|^B462|^B463/, 'jet-generic'],
  // Regional / utility turboprops with wing nacelles.
  [/DASH8|DHC8|^DH8|Q400|^DH4|DASH7|DHC7|^DHC7|^DH7|CC295|KINGFISHER|^C295|^CN35/, 'turboprop-generic'],
  [/PC12|PILATUS|^PC1/, 'pc12'],
  [/KINGAIR|KING AIR|B350|BE20|BE30|BE9|SUPERKING|^B35/, 'kingair350'],
];

/**
 * Map a free-text aircraft_type to a silhouette key. Specific airframes resolve
 * directly; everything else falls back through the airframe-kind heuristic
 * (helicopter → rotor-generic, jet → jet-generic, fixed → fixed-wing), and an
 * empty/absent type yields the unknown marker.
 */
export function resolveSilhouette(aircraftType: string | null): SilhouetteKey {
  const raw = (aircraftType ?? '').trim();
  if (!raw) return 'unknown';
  const norm = raw.toUpperCase().replace(/[\s-]/g, '');
  for (const [re, key] of RULES) {
    // Test against both the spaced original (for 'KING AIR') and normalised form.
    if (re.test(norm) || re.test(raw.toUpperCase())) return key;
  }
  // Fall back on the airframe-kind heuristic. Feed it the normalised code so
  // anchored fragments (^S76, ^AW1) still match dashed labels like 'S-76C+'.
  const kind: AircraftKind = kindFromType(norm, raw);
  if (kind === 'heli') return 'rotor-generic';
  if (kind === 'jet') return 'jet-generic';
  return 'fixedwing-generic';
}

const urlCache = new Map<SilhouetteKey, string>();

/**
 * Resolve an aircraft silhouette to a cached data-URL. The marker is the
 * constant one-colour fleet treatment baked into the asset (option B ruling —
 * no per-role tint parameter). Heading is NOT baked here — the caller rotates
 * the billboard via Cesium's native `rotation`, so the same data-URL serves
 * every heading (one cached texture per airframe type).
 */
export function aircraftSilhouetteUrl(aircraftType: string | null): string {
  const key = resolveSilhouette(aircraftType);
  const hit = urlCache.get(key);
  if (hit !== undefined) return hit;
  const url = `data:image/svg+xml,${encodeURIComponent(AIRCRAFT_SILHOUETTE[key])}`;
  urlCache.set(key, url);
  return url;
}
